import React, { useContext } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import { FormContext } from "../../contexts/FormContext";
import { colors } from "../../themes";
import { colorFromTheme, hsl } from "../../utils/color";
import constants from "../../utils/constants";
import { glowTransition } from "../../utils/styles";
import IconButtonMenuArrowLine from "../icons/IconButtonMenuArrowLine";

import ButtonLoadingIcon from "./ButtonLoadingIcon";

export const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
`;
export const ButtonLoading = styled.div`
  display: flex;
  align-items: center;
`;
export const ButtonMenu = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 1rem;
  }
`;

export const ButtonBadge = styled.div`
  position: absolute;
  top: calc(100% - 8px);
  left: calc(100% - 8px);
  box-sizing: border-box;

  // these sizes are pretty fiddly if you mess with them.
  border-radius: 50%;

  font-size: 1rem;

  min-width: 3.5ch;
  min-height: 3.5ch;
  line-height: 3.5ch;

  background-color: ${colorFromTheme("primary")};
  color: ${colorFromTheme("primary", "contrastTextColor")};
`;

const baseStyles = css<StyledButtonProps>`
  align-items: center;
  background-color: transparent;
  border-radius: ${constants.lengths.sm};
  user-select: none;
  border: solid 1px transparent;
  box-sizing: border-box;
  color: ${({ color = "secondary", ...props }) =>
    hsl(props.theme.colors?.[color]?.textColor) ||
    hsl(props.theme.colors?.[color]?.color)};
  display: flex;
  font-family: inherit;
  font-size: 1.2rem;
  outline: none;
  transition:
    border-color 0.2s,
    background-color 0.1s,
    color 0.2s,
    opacity 0.2s;
  white-space: nowrap;
  position: relative;

  ${({ size = "md" }) => {
    switch (size) {
      case "lg":
        return `
        height: 3.6rem;
        padding: 1rem;
        gap: 1rem;
      `;
      case "sm":
        return `
        height: 2.4rem;
        padding: 0.5rem;
        gap: 0.5rem;
      `;
      case "md":
        return `
        height: 3rem;
        padding: 0.8rem;
        gap: 0.8rem;
      `;
    }
  }}

  svg {
    fill: currentColor;
  }

  /* todo: this is weird */
  ${({ color = "secondary", ...props }) => `\
      --h: ${props.theme.colors?.[color]?.color[0]};
      --s: ${props.theme.colors?.[color]?.color[1]}%;
      --l: ${props.theme.colors?.[color]?.color[2]}%;
    `}

  ${({ disabled }) =>
    disabled
      ? `
      cursor: not-allowed;
      opacity: 0.5;  
    `
      : `
      cursor: pointer;
    `}

  ${({ stretch }) =>
    stretch &&
    `
      width: 100%;
      justify-content: center;
  `}

  // set the glow color if it exists, otherwise use the primary color 
  // of the button
  ${({ theme, color = "secondary" }) => css`
    --input-glow-color: ${() =>
      hsl(
        theme.colors?.[color]?.glowColor ?? theme.colors?.[color]?.color,
        0.4,
      )};
  `} 

  &:focus-visible {
    box-shadow: 0 0 0px 0px var(--input-glow-color);
    animation: ${glowTransition} 0.3s 1;
    animation-fill-mode: forwards;
  }

  &:hover {
    /* border-color: hsl(var(--h), var(--s), calc(var(--l) - 20%)); */
    background-color: hsla(var(--h), var(--s), var(--l), 0.15);
  }

  &:not([disabled]):active {
    background-color: hsla(var(--h), var(--s), var(--l), 0.3);
  }

  ${ButtonMenu} {
    svg {
      fill: ${({ iconColor = "secondary", ...props }) =>
        iconColor ? props.theme.colors?.[iconColor]?.color : "currentColor"};
    }
  }

  ${ButtonIcon} {
    svg {
      fill: ${({ iconColor = "secondary", ...props }) =>
        iconColor ? props.theme.colors?.[iconColor]?.color : "currentColor"};
      height: ${({ size = "md" }) => {
        switch (size) {
          case "lg":
            return `1.4rem`;
          case "sm":
            return `1.0rem`;
          default:
            return `1.2rem`;
        }
      }};
    }
  }
  ${ButtonLoading} {
    svg {
      fill: ${({ iconColor = "secondary", ...props }) =>
        iconColor ? props.theme.colors?.[iconColor]?.color : "currentColor"};
      height: ${({ size = "md" }) => {
        switch (size) {
          case "lg":
            return `1.8rem`;
          case "sm":
            return `1.0rem`;
          default:
            return `1.6rem`;
        }
      }};
    }
  }
`;

const containedStyles = css<StyledButtonProps>`
  ${({ color = "secondary" }) => color.match(/secondary/) && "--l: 90%;"}

  background-color: hsl(var(--h), var(--s), var(--l));
  border: solid 1px hsl(var(--h), var(--s), calc(var(--l) - 8%));
  color: ${({ color = "secondary", ...props }) =>
    hsl(props.theme.colors?.[color]?.contrastTextColor)};

  &:hover {
    border-color: hsl(var(--h), var(--s), calc(var(--l) - 16%));
    background-color: hsl(var(--h), var(--s), calc(var(--l) - 4%));
    box-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.1);
  }
  &:not([disabled])&:active {
    box-shadow:
      inset 0 0 0px 1px hsla(0, 0%, 0%, 0.05),
      0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.1);
    background-color: hsl(var(--h), var(--s), calc(var(--l) + 0%));
  }
  &:disabled {
    background-color: hsl(var(--h), var(--s), calc(var(--l) + 0%));
  }
`;

const outlinedStyles = css<StyledButtonProps>`
  color: ${({ color = "secondary", ...props }) =>
    hsl(props.theme.colors?.[color]?.textColor) ||
    hsl(props.theme.colors?.[color]?.color)};

  border: solid 1px hsla(var(--h), var(--s), var(--l), 0.5);

  &:hover {
    border-color: hsla(var(--h), var(--s), var(--l), 0.8);
  }
`;

const StyledButton = styled.button<StyledButtonProps>`
  ${baseStyles}

  ${({ variant = "contained" }) => {
    switch (variant) {
      case "outlined":
        return outlinedStyles;
      case "contained":
        return containedStyles;
    }
  }}
`;

type StyledButtonProps = {
  size?: "sm" | "md" | "lg";
  variant?: "plain" | "outlined" | "contained";
  color?: colors;
  iconColor?: colors;
  disabled?: boolean;
  stretch?: boolean;
  isLoading?: boolean;
  menu?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  hasBadge?: boolean;
  badgeCount?: number;
};

// Props for the styled button
export type ButtonProps = StyledButtonProps &
  React.HTMLAttributes<HTMLButtonElement> & {
    title: string;
  };

// we do not need the title prop in the style-component types

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const form = useContext(FormContext);

    return (
      <StyledButton
        {...props}
        disabled={props.disabled ?? form.disabled}
        aria-label={props.title}
        ref={ref}
      >
        {props.isLoading ? (
          <ButtonLoading>
            <ButtonLoadingIcon />
          </ButtonLoading>
        ) : props.icon ? (
          <ButtonIcon>{props.icon}</ButtonIcon>
        ) : null}

        {props.children}

        {props.menu && (
          <ButtonMenu>
            <IconButtonMenuArrowLine />
          </ButtonMenu>
        )}
        {props.hasBadge && (
          <ButtonBadge>
            {props.badgeCount && props.badgeCount > 99
              ? "99+"
              : props.badgeCount}
          </ButtonBadge>
        )}
      </StyledButton>
    );
  },
);

const _Button = React.memo(ButtonComponent);
const Button = styled(_Button)``;

type LinkButtonProps = StyledButtonProps & {
  to: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};
const StyledLinkButton = styled(Link)<LinkButtonProps>`
  ${baseStyles}

  text-decoration: none;

  ${({ variant = "contained" }) => {
    switch (variant) {
      case "outlined":
        return outlinedStyles;
      case "contained":
        return containedStyles;
    }
  }}
`;

const LinkButtonComponent = (props: LinkButtonProps) => {
  return (
    <StyledLinkButton {...props}>
      {props.isLoading ? (
        <ButtonLoading>
          <ButtonLoadingIcon />
        </ButtonLoading>
      ) : props.icon ? (
        <ButtonIcon>{props.icon}</ButtonIcon>
      ) : null}

      {props.children}

      {props.menu && (
        <ButtonMenu>
          <IconButtonMenuArrowLine />
        </ButtonMenu>
      )}
      {props.hasBadge && (
        <ButtonBadge>
          {props.badgeCount && props.badgeCount > 99 ? "99+" : props.badgeCount}
        </ButtonBadge>
      )}
    </StyledLinkButton>
  );
};

export const LinkButton = styled(LinkButtonComponent)``;

export default Button;
